{
  "name": "Modern Tribes",
  "description": {
    "en": [
      "- Modern Tribes is a collaboration platform for teams (tribes) and their projects",
      "- Organize work into projects with customizable feature tabs — tasks, documents, events and more",
      "- Track your own tasks and events from a personal dashboard, collaborate with your team, and stay informed through notifications"
    ],
    "fr": [
      "- Modern Tribes est une plateforme de collaboration pour les équipes (tribus) et leurs projets",
      "- Organisez votre travail en projets avec des onglets personnalisables — tâches, documents, événements et plus",
      "- Suivez vos propres tâches et événements depuis un tableau de bord personnel, collaborez avec votre équipe et restez informé"
    ]
  },
  "backend": {
    "stack": [
      "postgres for the database",
      "python fastapi for the backend"
    ]
  },
  "frontend": {
    "stack": [
      "typescript",
      "react"
    ]
  },
  "platform": {
    "core": [
      {
        "package": "authentication",
        "description": {
          "en": [
            "- sign in with a magic link sent to your email — no password needed",
            "- once a device is signed in, it stays signed in as long as it is used at least once a year; if you are ever signed out, the login page shows the reason and technical detail"
          ],
          "fr": [
            "- connectez-vous avec un lien magique envoyé par email — pas de mot de passe",
            "- une fois un appareil connecté, il le reste tant qu'il est utilisé au moins une fois par an ; en cas de déconnexion, la page de connexion affiche la raison et le détail technique"
          ]
        },
        "backend": {
          "schema": [
            "users",
            "users_sessions"
          ],
          "path": [
            "backend/app/platform/core/authentication/**"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/platform/core/authentication/**"
          ]
        }
      },
      {
        "package": "authorization",
        "description": {
          "en": [
            "- controls what each user can see and do based on their role and tribe membership",
            "- members and managers have different levels of access to content"
          ],
          "fr": [
            "- contrôle ce que chaque utilisateur peut voir et faire selon son rôle et son appartenance à une tribu",
            "- les membres et gestionnaires ont des niveaux d'accès différents"
          ]
        },
        "backend": {
          "schema": [
            "users_roles",
            "roles",
            "roles_permissions"
          ],
          "path": [
            "backend/app/platform/core/authorization/**"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/platform/core/authorization/**"
          ]
        }
      },
      {
        "package": "api",
        "description": {
          "en": [
            "- all your actions are sent to the server in real time — connection issues are handled automatically"
          ],
          "fr": [
            "- toutes vos actions sont envoyées au serveur en temps réel — les problèmes de connexion sont gérés automatiquement"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/platform/core/api/**"
          ]
        }
      },
      {
        "package": "browser",
        "description": {
          "en": [
            "- your preferences and session are stored in the browser so the app remembers your settings between visits"
          ],
          "fr": [
            "- vos préférences et votre session sont stockées dans le navigateur pour retrouver vos réglages entre les visites"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/platform/core/browser/**"
          ]
        }
      },
      {
        "package": "layout",
        "description": {
          "en": [
            "- the app's visual structure: header, navigation menus and breadcrumbs",
            "- shared UI controls used throughout the app: buttons, text fields, modals, tables, icons and calendars",
            "- an optional icon-only toolbar for the current tab's and page's actions, so they don't require opening the menu; from your profile you can choose to show it in the header, in the footer, or keep it hidden; the tab's actions and the page's actions are separated by a divider, and once there are more than a few actions in total the page's actions tuck into an expandable menu automatically — a \"configure toolbar\" action, available everywhere, lets you decide yourself which actions stay directly visible and which go in that menu, remembered per tab",
            "- a single icon on the footer hides the header, the toolbar and the footer all together, leaving only that icon and a small page title behind to bring them all back — remembered on this device"
          ],
          "fr": [
            "- la structure visuelle de l'app : en-tête, menus de navigation et fil d'Ariane",
            "- contrôles partagés utilisés dans toute l'app : boutons, champs, modales, tableaux, icônes et calendriers",
            "- une barre d'outils optionnelle avec des icônes pour les actions de l'onglet et de la page en cours, sans avoir à ouvrir le menu ; depuis votre profil, vous pouvez choisir de l'afficher dans l'en-tête, dans le pied de page, ou de la garder masquée ; les actions de l'onglet et celles de la page sont séparées par une barre verticale, et au-delà de quelques actions, celles de la page se replient automatiquement dans un petit menu déroulant — une action « configurer la barre d'outils », disponible partout, vous permet de choisir vous-même quelles actions restent visibles directement et lesquelles vont dans ce menu, mémorisé par onglet",
            "- une seule icône sur le pied de page masque l'en-tête, la barre d'outils et le pied de page tous ensemble, ne laissant que cette icône et un petit titre de page pour tout remettre en place — mémorisé sur cet appareil"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/platform/core/layout/**"
          ]
        }
      },
      {
        "package": "app-config",
        "description": {
          "en": [
            "- app-wide settings: logo, app name and which features are enabled for users"
          ],
          "fr": [
            "- paramètres globaux de l'app : logo, nom de l'application et fonctionnalités accessibles aux utilisateurs"
          ]
        },
        "backend": {
          "schema": [
            "app_config"
          ],
          "path": [
            "backend/app/platform/core/app_config/**"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/platform/core/app-config/**"
          ]
        }
      },
      {
        "package": "responsive",
        "description": {
          "en": [
            "- the app adapts its layout to your screen size — works on phones, tablets and desktops"
          ],
          "fr": [
            "- l'app adapte sa mise en page à la taille de votre écran — fonctionne sur téléphone, tablette et ordinateur"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/platform/core/responsive/**"
          ]
        }
      },
      {
        "package": "i18n",
        "description": {
          "en": [
            "- available in English and French",
            "- switch languages from your profile — your preference is saved"
          ],
          "fr": [
            "- disponible en anglais et en français",
            "- changez de langue depuis votre profil — votre préférence est enregistrée"
          ]
        },
        "backend": {
          "schema": [
            "users.language"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/platform/core/i18n/**"
          ]
        }
      }
    ],
    "functions": [
      {
        "package": "labels",
        "description": {
          "en": [
            "- create color labels and tag tasks, documents and other items to organize and filter them quickly"
          ],
          "fr": [
            "- créez des étiquettes colorées et balisez les tâches, documents et autres éléments pour les organiser et filtrer facilement"
          ]
        },
        "backend": {
          "schema": [
            "labels"
          ],
          "path": [
            "backend/app/platform/functions/labels/**"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/platform/functions/labels/**"
          ]
        }
      },
      {
        "package": "monitoring",
        "description": {
          "en": [
            "- see a live feed of what changed across your projects — documents edited, tasks updated and more",
            "- review the full history of any document to see what was modified and when"
          ],
          "fr": [
            "- consultez un fil d'activité en direct sur vos projets — documents modifiés, tâches mises à jour et plus",
            "- consultez l'historique complet d'un document pour voir ce qui a été modifié et quand"
          ]
        },
        "backend": {
          "path": [
            "backend/app/platform/functions/monitoring/**"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/platform/functions/monitoring/**"
          ]
        }
      },
      {
        "package": "publications",
        "description": {
          "en": [
            "- users can publish or unpublish project documents",
            "- published documents are accessible publicly"
          ],
          "fr": [
            "- les utilisateurs peuvent publier ou dépublier des documents de projet",
            "- les documents publiés sont accessibles publiquement"
          ]
        },
        "backend": {
          "schema": [
            "publications"
          ],
          "path": [
            "backend/app/platform/functions/publications/**"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/platform/functions/publications/**"
          ]
        }
      },
      {
        "package": "search",
        "description": {
          "en": [
            "- search across all your tasks, documents, kanban cards and labels in one place",
            "- matching text is highlighted on the result page for quick reading",
            "- labels applied to items are also searchable"
          ],
          "fr": [
            "- recherchez dans toutes vos tâches, documents, cartes kanban et étiquettes en un seul endroit",
            "- le texte correspondant est mis en évidence sur la page de résultat pour une lecture rapide",
            "- les étiquettes appliquées aux éléments sont également incluses dans la recherche"
          ]
        },
        "backend": {
          "schema": [
            "search_index"
          ],
          "path": [
            "backend/app/platform/functions/search/**"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/platform/functions/search/**"
          ]
        }
      },
      {
        "package": "people",
        "description": {
          "en": [
            "- manage the members of your tribe and their profiles",
            "- a person can be linked to the account they use to sign in"
          ],
          "fr": [
            "- gérez les membres de votre tribu et leurs profils",
            "- une personne peut être liée au compte qu'elle utilise pour se connecter"
          ]
        },
        "backend": {
          "schema": [
            "users",
            "persons",
            "represents"
          ],
          "path": [
            "backend/app/platform/functions/people/**"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/platform/functions/people/**"
          ]
        }
      },
      {
        "package": "documents",
        "description": {
          "en": [
            "- create and edit rich-text documents with multiple pages, file attachments and a full revision history",
            "- wherever you write a longer description (a recipe, a meal, a song, a journal entry, a tribe, a project, a task, an event...), it auto-saves itself every minute while you're changing it, and '<'/'>' next to Save/Cancel let you step back through earlier versions and bring one back by editing it further and saving"
          ],
          "fr": [
            "- créez et éditez des documents riches avec plusieurs pages, pièces jointes et un historique complet des révisions",
            "- partout où vous rédigez une description un peu longue (une recette, un repas, une chanson, une entrée de journal, une tribu, un projet, une tâche, un événement...), elle s'enregistre automatiquement toutes les minutes pendant que vous la modifiez, et '<'/'>' à côté d'Enregistrer/Annuler permettent de revenir sur une version précédente et de la reprendre en l'éditant puis en l'enregistrant"
          ]
        },
        "backend": {
          "schema": [
            "documents",
            "document_pages",
            "document_attachments"
          ],
          "path": [
            "backend/app/platform/functions/documents/**"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/platform/functions/documents/**"
          ]
        }
      }
    ],
    "tools": [
      {
        "package": "notifications",
        "description": {
          "en": [
            "- receive in-app alerts for important events in your projects",
            "- view all your notifications and mark them as read",
            "- admins can broadcast a notification to all users"
          ],
          "fr": [
            "- recevez des alertes dans l'app pour les événements importants de vos projets",
            "- consultez toutes vos notifications et marquez-les comme lues",
            "- les administrateurs peuvent envoyer une notification à tous les utilisateurs"
          ]
        },
        "backend": {
          "schema": [
            "notifications"
          ],
          "path": [
            "backend/app/platform/tools/notifications/**"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/platform/tools/notifications/**"
          ]
        }
      },
      {
        "package": "pwa",
        "description": {
          "en": [
            "- install the app on your phone or computer for quick access — works on iOS, Android and desktop",
            "- a guided install page walks you through the steps for your device",
            "- the install link is included in your sign-in email",
            "- receive push notifications on your device even when the app is closed — event reminders and alerts arrive instantly"
          ],
          "fr": [
            "- installez l'app sur votre téléphone ou ordinateur pour un accès rapide — iOS, Android et bureau",
            "- une page d'installation guidée vous accompagne selon votre appareil",
            "- le lien d'installation est inclus dans votre email de connexion",
            "- recevez des notifications push sur votre appareil même quand l'app est fermée — rappels d'événements et alertes arrivent instantanément"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/platform/tools/pwa/**",
            "frontend/public/.well-known/assetlinks.json"
          ]
        },
        "android": {
          "path": [
            "frontend/src/android/**",
            "scripts/build-android-apk.sh"
          ]
        }
      },
      {
        "package": "revisions",
        "description": {
          "en": [
            "- browse the full edit history of a document and see how it looked at any point in time"
          ],
          "fr": [
            "- parcourez l'historique complet des modifications d'un document et voyez son état à n'importe quel moment"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/platform/tools/revisions/**"
          ]
        }
      },
      {
        "package": "mail",
        "description": {
          "en": [
            "- the app sends emails for sign-in links and other notifications",
            "- admins can review the log of sent and pending emails"
          ],
          "fr": [
            "- l'app envoie des emails pour les liens de connexion et autres notifications",
            "- les administrateurs peuvent consulter le journal des emails envoyés et en attente"
          ]
        },
        "backend": {
          "schema": [
            "mails",
            "mail_recipients"
          ],
          "path": [
            "backend/app/platform/tools/mail/**"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/platform/tools/mail/**"
          ]
        }
      }
    ]
  },
  "features": {
    "description": {
      "en": [
        "- features are tools that can be added to a project as tabs (tasks, documents, guitar tools…)",
        "- a project can have several tabs of the same type — each is a separate, independent instance",
        "- the project manager chooses which features appear as tabs in each project"
      ],
      "fr": [
        "- les fonctionnalités sont des outils ajoutables à un projet sous forme d'onglets (tâches, documents, guitare…)",
        "- un projet peut avoir plusieurs onglets du même type — chacun est une instance séparée et indépendante",
        "- le gestionnaire du projet choisit quelles fonctionnalités apparaissent comme onglets"
      ]
    },
    "features": [
      {
        "package": "bookmarks",
        "description": {
          "en": [
            "- save shortcuts to your most-visited pages with custom colors and your preferred order"
          ],
          "fr": [
            "- enregistrez des raccourcis vers vos pages les plus visitées avec des couleurs personnalisées et votre ordre préféré"
          ]
        },
        "backend": {
          "schema": [
            "user_bookmarks"
          ],
          "path": [
            "backend/app/features/bookmarks/**"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/features/bookmarks/**"
          ]
        }
      },
      {
        "package": "tasks",
        "description": {
          "en": [
            "- manage your team's work with two views: a to-do list and a visual kanban board",
            "- assign tasks to people, add labels, set or clear due dates and attach detailed notes; rename, recolour, reorder or delete labels from one panel",
            "- set reminders on any task or card to get notified by push notification or email at a chosen date and time"
          ],
          "fr": [
            "- gérez le travail de votre équipe avec deux vues : une liste de tâches et un tableau kanban",
            "- assignez des tâches, ajoutez des étiquettes, définissez ou supprimez des échéances et joignez des notes détaillées ; renommez, recolorez, réordonnez ou supprimez les étiquettes depuis un même panneau",
            "- définissez des rappels sur n'importe quelle tâche ou carte pour être notifié par notification push ou email à la date et l'heure choisies"
          ]
        },
        "backend": {
          "path": [
            "backend/app/features/tasks/**"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/features/tasks/**"
          ]
        },
        "sub_packages": [
          {
            "package": "todo_list",
            "description": {
              "en": [
                "- a scrollable task list you can filter and check off one by one"
              ],
              "fr": [
                "- une liste de tâches défilante que vous pouvez filtrer et cocher une par une"
              ]
            },
            "backend": {
              "schema": [
                "todo_items",
                "todo_labels"
              ],
              "path": [
                "backend/app/features/tasks/todo_list/**"
              ]
            },
            "frontend": {
              "path": [
                "frontend/src/app/features/tasks/todo_list/**"
              ]
            }
          },
          {
            "package": "kanban",
            "description": {
              "en": [
                "- a board with drag-and-drop columns — move cards between stages to track your team's progress",
                "- reorder cards and manage columns as your workflow evolves"
              ],
              "fr": [
                "- un tableau avec des colonnes déplaçables — déplacez des cartes entre les étapes pour suivre l'avancement",
                "- réorganisez les cartes et gérez les colonnes selon votre workflow"
              ]
            },
            "backend": {
              "schema": [
                "kanban_columns",
                "kanban_cards",
                "kanban_labels"
              ],
              "path": [
                "backend/app/features/tasks/kanban/**"
              ]
            },
            "frontend": {
              "path": [
                "frontend/src/app/features/tasks/kanban/**"
              ]
            }
          },
          {
            "package": "my_tasks",
            "description": {
              "en": [
                "- see all tasks assigned to you across every project in one place",
                "- filter by tribe, project, person or label to focus on what matters"
              ],
              "fr": [
                "- consultez toutes les tâches qui vous sont assignées dans tous vos projets en un seul endroit",
                "- filtrez par tribu, projet, personne ou étiquette pour vous concentrer sur l'essentiel"
              ]
            },
            "backend": {
              "path": [
                "backend/app/features/tasks/my_tasks/**"
              ]
            },
            "frontend": {
              "path": [
                "frontend/src/app/features/tasks/my_tasks/**"
              ]
            }
          }
        ]
      },
      {
        "package": "groceries",
        "description": {
          "en": [
            "- keep a shared shopping catalog of items (with a unit, how often you typically restock them, and a suggested quantity that pre-fills when you add it to a list) and reusable aisle sections like \"Butchery\" or \"Produce\", filterable by name and reorderable to match how you walk the aisles when building a list — browse and manage the catalog directly from the tab, without opening a list first; mark a section as a food aisle or not, so only food aisles are offered when picking a recipe's ingredients, and mark a section as condiments so a recipe shows its condiment ingredients set apart",
            "- schedule shopping lists to a date you can change or clear later, or leave undated for now, so they show up in your dashboard planning — a list you didn't get to in time shows as passed, while an undated list never does; rename it (or leave it untitled to show as \"Next shopping\"), archive once it's done, mark a list as a favorite, and start a new list from a favorite's content",
            "- there's always an ongoing list to shop from: opening the tab takes you straight into it when there's exactly one, and starts a fresh untitled, undated one for you when there isn't any",
            "- check items off as you put them in your cart, and see at a glance what's left to buy",
            "- get suggestions for items you're due to restock, and for ingredients needed by any meal planned after the list's date, shown day by day in date order; adding a suggested ingredient tops up the quantity of a matching item already on your list instead of adding a duplicate line — a recipe's core ingredients are added all at once, while its accompaniments (suggested sides) are added one by one; a meal already added can be removed again (with a confirmation) so it stops being accounted for on this list",
            "- add a note to any item — shown behind a small info icon so it doesn't crowd the list, with lines starting with \"* \" displayed as a bullet list — and add one-off items just for a single list without putting them in the shared catalog"
          ],
          "fr": [
            "- gérez un catalogue d'articles partagé (avec une unité, une fréquence de réapprovisionnement habituelle et une quantité suggérée préremplie lors de l'ajout à une liste) et des rayons réutilisables comme « Boucherie » ou « Fruits & Légumes », filtrables par nom et réordonnables selon l'ordre dans lequel vous parcourez les rayons — parcourez et gérez le catalogue directement depuis l'onglet, sans avoir à ouvrir une liste ; marquez un rayon comme alimentaire ou non, pour ne proposer que les rayons alimentaires lors du choix des ingrédients d'une recette, et marquez un rayon comme condiments pour qu'une recette affiche ses ingrédients condiments à part",
            "- programmez vos listes de courses à une date que vous pouvez changer ou supprimer ensuite, ou laissez-les sans date pour l'instant, pour qu'elles apparaissent dans la planification de votre tableau de bord — une liste que vous n'avez pas traitée à temps apparaît comme passée, une liste sans date jamais ; renommez-la (ou laissez-la sans titre pour qu'elle s'affiche comme « Prochaines courses »), archivez-la une fois terminée, marquez-la comme favorite, et repartez d'une liste favorite pour créer une nouvelle liste",
            "- il y a toujours une liste en cours pour faire vos courses : ouvrir l'onglet vous y emmène directement s'il n'y en a qu'une, et vous en ouvre une nouvelle sans titre ni date s'il n'y en a aucune",
            "- cochez les articles au fur et à mesure que vous les mettez dans votre panier, et voyez en un coup d'œil ce qu'il reste à acheter",
            "- recevez des suggestions d'articles à racheter, ainsi que des ingrédients nécessaires pour tout repas planifié après la date de la liste, présentées jour par jour et triées par date ; ajouter un ingrédient suggéré augmente la quantité d'un article déjà présent sur votre liste au lieu d'en créer un doublon — les ingrédients principaux d'une recette s'ajoutent tous d'un coup, tandis que ses accompagnements (suggestions à part) s'ajoutent un par un ; un repas déjà ajouté peut être retiré (avec confirmation) pour ne plus être pris en compte dans cette liste",
            "- ajoutez une note à n'importe quel article — affichée derrière une petite icône d'info pour ne pas encombrer la liste, les lignes commençant par « * » s'affichant en liste à puces — et ajoutez des articles ponctuels propres à une seule liste sans les mettre dans le catalogue partagé"
          ]
        },
        "backend": {
          "schema": [
            "groceries_items",
            "groceries_sections",
            "groceries_item_sections",
            "groceries_instance_items",
            "groceries_lists",
            "groceries_list_items"
          ],
          "path": [
            "backend/app/features/groceries/**"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/features/groceries/**"
          ]
        }
      },
      {
        "package": "tribes-projects",
        "description": {
          "en": [
            "- tribes are your teams — each tribe has members, named positions and its own projects",
            "- projects host the feature tabs (tasks, documents…) where work happens",
            "- apply a color theme at the tribe, project or tab level to personalize the look"
          ],
          "fr": [
            "- les tribus sont vos équipes — chaque tribu a des membres, des postes nommés et ses propres projets",
            "- les projets hébergent les onglets de fonctionnalités (tâches, documents…) où le travail se fait",
            "- appliquez un thème de couleur au niveau de la tribu, du projet ou de l'onglet pour personnaliser l'apparence"
          ]
        },
        "backend": {
          "schema": [
            "tribes",
            "projects",
            "positions",
            "feature_instances",
            "project_documents"
          ],
          "path": [
            "backend/app/features/tribes_projects/**"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/features/tribes-projects/**"
          ]
        }
      },
      {
        "package": "glue",
        "description": {
          "en": [
            "- attach tools (task lists, kanban boards, guitar tools…) to a project as named tabs, each with its own icon",
            "- each user can click on any tab to show, hide, reorder it, or personalize its icon, name and color, just for themselves"
          ],
          "fr": [
            "- ajoutez des outils (listes de tâches, kanbans, outils guitare…) à un projet sous forme d'onglets nommés, chacun avec sa propre icône",
            "- chaque utilisateur peut cliquer sur n'importe quel onglet pour l'afficher, le masquer, le réordonner, ou personnaliser son icône, son nom et sa couleur, juste pour lui"
          ]
        },
        "backend": {
          "schema": [
            "projects_features",
            "user_tab_configs",
            "dashboard_pinned_tabs"
          ],
          "path": [
            "backend/app/features/glue/**"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/features/glue/**"
          ]
        }
      },
      {
        "package": "dashboard",
        "description": {
          "en": [
            "- personalized dashboard showing your tasks, tribes and bookmarks",
            "- Planning tab: a day-by-day (or week) view of all your events and tasks due, with filters, and quick-add for new tasks or events",
            "- Projects, Tasks and Events tabs: browse everything you have access to across your tribes, with open/upcoming counts, filterable by tribe",
            "- pin any feature tab (tasks, kanban, journal…) as a dashboard tab to view its content directly from the dashboard",
            "- jump straight from any task or event shown here to its project",
            "- customize each tab's color, visibility and order by clicking it, and choose your default project and tab for quick-add by picking a tribe then a project"
          ],
          "fr": [
            "- tableau de bord personnalisé affichant vos tâches, tribus et favoris",
            "- onglet Planning : vue jour par jour (ou semaine) de tous vos événements et tâches à rendre, avec filtres, et ajout rapide de tâches ou d'événements",
            "- onglets Projets, Tâches et Événements : parcourez tout ce à quoi vous avez accès dans vos tribus, avec le nombre d'éléments ouverts/à venir, filtrable par tribu",
            "- épinglez n'importe quel onglet de fonctionnalité (tâches, kanban, journal…) comme onglet du tableau de bord pour en voir le contenu directement",
            "- accédez directement depuis une tâche ou un événement affiché ici à son projet",
            "- personnalisez la couleur, la visibilité et l'ordre de chaque onglet en cliquant dessus, et choisissez votre projet et onglet par défaut pour l'ajout rapide en sélectionnant une tribu puis un projet"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/features/glue/dashboard/**"
          ]
        }
      },
      {
        "package": "events",
        "description": {
          "en": [
            "- create and manage events within a project tab, with a start/end time or spanning the whole day or several days",
            "- assign participants from the project tribe, attach labels, story points and notes; rename, recolour, reorder or delete labels from one panel",
            "- pick a color for each event, shown on the calendar and day view",
            "- set reminders that notify participants by in-app alert or email before the event",
            "- view events on a monthly calendar, a day timeline with overlapping events side by side, or a week view",
            "- pick times with a clock-face time picker and quick duration buttons"
          ],
          "fr": [
            "- créez et gérez des événements dans un onglet de projet, avec une heure de début/fin ou sur une ou plusieurs journées entières",
            "- assignez des participants depuis la tribu du projet, ajoutez des étiquettes, des points d'effort et des notes ; renommez, recolorez, réordonnez ou supprimez les étiquettes depuis un même panneau",
            "- choisissez une couleur pour chaque événement, affichée sur le calendrier et dans la vue du jour",
            "- définissez des rappels qui notifient les participants par alerte dans l'app ou par email avant l'événement",
            "- visualisez les événements sur un calendrier mensuel, une ligne de temps du jour avec chevauchements côte à côte, ou une vue semaine",
            "- choisissez les heures avec un cadran d'horloge et des boutons de réglage rapide de durée"
          ]
        },
        "backend": {
          "schema": [
            "events",
            "events_participants",
            "events_reminders"
          ],
          "path": [
            "backend/app/features/events/**"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/features/events/**"
          ]
        }
      },
      {
        "package": "guitar",
        "description": {
          "en": [
            "- a set of tools to support your guitar practice",
            "- includes a note quiz, a chromatic tuner, a metronome, a chord inventory and a songbook"
          ],
          "fr": [
            "- un ensemble d'outils pour soutenir votre pratique de la guitare",
            "- comprend un quiz de notes, un accordeur chromatique, un métronome, un inventaire d'accords et un répertoire de morceaux"
          ]
        },
        "sub_packages": [
          {
            "package": "notes",
            "description": {
              "en": [
                "- a random note is played and displayed — guess it before it's revealed, using the fretboard to show which string and fret to play; every other occurrence of that same note elsewhere on the neck is circled, so you can see all its octaves at a glance",
                "- customize the interval, sound length, octave range and which notes to include — saved per tab"
              ],
              "fr": [
                "- une note aléatoire est jouée et affichée — devinez-la avant qu'elle soit révélée, le manche montre la corde et la frette à jouer ; chaque autre occurrence de cette même note ailleurs sur le manche est entourée, pour voir d'un coup d'œil toutes ses octaves",
                "- personnalisez l'intervalle, la durée du son, la plage d'octaves et les notes à inclure — sauvegardé par onglet"
              ]
            },
            "backend": {
              "path": [
                "backend/app/features/guitar/notes/**"
              ]
            },
            "frontend": {
              "path": [
                "frontend/src/app/features/guitar/notes/**"
              ]
            }
          },
          {
            "package": "tuner",
            "description": {
              "en": [
                "- tune your guitar using your device's microphone — the app listens and shows the closest note in real time",
                "- a needle meter shows how flat or sharp you are; the note name turns green when you're in tune",
                "- displays the detected note name, octave, frequency and deviation in cents"
              ],
              "fr": [
                "- accordez votre guitare grâce au microphone de votre appareil — l'app écoute et affiche la note la plus proche",
                "- une aiguille montre si vous êtes trop bas ou trop haut ; le nom de la note devient vert quand vous êtes accordé",
                "- affiche le nom de la note, l'octave, la fréquence et l'écart en centièmes"
              ]
            },
            "backend": {
              "path": [
                "backend/app/features/guitar/tuner/**"
              ]
            },
            "frontend": {
              "path": [
                "frontend/src/app/features/guitar/tuner/**",
                "frontend/src/app/features/guitar/audioContext.ts"
              ]
            }
          },
          {
            "package": "metronome",
            "description": {
              "en": [
                "- keep time during your practice with an adjustable click track (20–300 BPM) and a visual beat indicator",
                "- set the time signature (2–8 beats per bar) and tap to find your tempo by feel — saved automatically per tab"
              ],
              "fr": [
                "- gardez le tempo pendant votre pratique avec un clic ajustable (20–300 BPM) et un indicateur visuel de temps",
                "- réglez la signature rythmique (2–8 temps par mesure) et tapotez pour trouver votre tempo à l'oreille — sauvegardé automatiquement par onglet"
              ]
            },
            "backend": {
              "path": [
                "backend/app/features/guitar/metronome/**"
              ]
            },
            "frontend": {
              "path": [
                "frontend/src/app/features/guitar/metronome/**"
              ]
            }
          },
          {
            "package": "chords",
            "description": {
              "en": [
                "- a shared inventory of guitar chord diagrams, built-in plus anything anyone adds",
                "- each chord shows which fret to press on each string, with icons marking the root, fifth and other intervals at a glance",
                "- name a chord and its root note is suggested automatically, or get a name suggested from the notes you picked",
                "- add a description to any chord, editable by anyone, and search the inventory by name, root note or the fret played on each string",
                "- optionally rate how hard a chord is to play, from 0 (easiest) to 5 (hardest)"
              ],
              "fr": [
                "- un inventaire d'accords de guitare partagé, avec des accords prédéfinis et ceux ajoutés par les utilisateurs",
                "- chaque accord indique la frette à presser sur chaque corde, avec des icônes marquant la fondamentale, la quinte et les autres intervalles en un coup d'œil",
                "- donnez un nom à un accord et sa fondamentale est suggérée automatiquement, ou obtenez un nom suggéré à partir des notes choisies",
                "- ajoutez une description à n'importe quel accord, modifiable par tous, et recherchez l'inventaire par nom, fondamentale ou frette jouée sur chaque corde",
                "- notez, si vous le souhaitez, la difficulté d'un accord à jouer, de 0 (le plus facile) à 5 (le plus difficile)"
              ]
            },
            "backend": {
              "path": [
                "backend/app/features/guitar/chords/**"
              ],
              "schema": [
                "guitar_chords"
              ]
            },
            "frontend": {
              "path": [
                "frontend/src/app/features/guitar/chords/**"
              ]
            }
          },
          {
            "package": "song",
            "description": {
              "en": [
                "- keep a shared songbook of the songs your group is practicing, searchable by title, author, lyrics, labels, difficulty and your own mastery level",
                "- each song has a title, author, tempo, time signature, capo and chord list, plus a rich-text description",
                "- lay out a song's page freely — chords, lyrics, chord grids, free-text blocks, videos — arranged in rows and columns exactly as it will read or print, with text size adjustable on a chord grid's chord names and on a free-text block's own content, or skip building a layout altogether and just upload a ready-made PDF sheet instead — chosen once when you add the song",
                "- switch to a clean presentation view for reading, printing or sharing, with a downloadable PDF",
                "- rate your own mastery of a song privately, and optionally rate its overall difficulty",
                "- mark a song as completed to lock it from accidental edits, or keep it as a draft while you build it",
                "- attach learning videos and a tempo-matched metronome to help you practice",
                "- tag songs with labels shared across the project's songbook"
              ],
              "fr": [
                "- tenez un répertoire partagé des morceaux que votre groupe travaille, cherchable par titre, auteur, paroles, labels, difficulté et votre propre niveau de maîtrise",
                "- un morceau a un titre, un auteur, un tempo, une signature rythmique, un capo et une liste d'accords, avec une description enrichie",
                "- composez librement la page d'un morceau — accords, paroles, tableaux d'accords, blocs de texte libre, vidéos — organisés en lignes et colonnes exactement comme il sera lu ou imprimé, avec une taille de texte réglable pour les noms d'accords d'un tableau et pour le contenu d'un bloc de texte libre, ou renoncez à composer une mise en page et importez directement une feuille PDF déjà prête — choisi une fois pour toutes à la création du morceau",
                "- passez à une vue présentation épurée pour la lecture, l'impression ou le partage, avec un PDF téléchargeable",
                "- notez votre propre maîtrise d'un morceau en privé, et notez si vous le souhaitez sa difficulté globale",
                "- marquez un morceau comme terminé pour le protéger des modifications accidentelles, ou gardez-le en brouillon pendant que vous le construisez",
                "- attachez des vidéos d'apprentissage et un métronome au tempo du morceau pour vous entraîner",
                "- étiquetez les morceaux avec des labels partagés dans tout le répertoire du projet"
              ]
            },
            "backend": {
              "path": [
                "backend/app/features/guitar/song/**"
              ],
              "schema": [
                "guitar_songs",
                "guitar_song_author",
                "guitar_songs_mastery",
                "guitar_songs_videos",
                "guitar_songs_layout_settings",
                "guitar_songs_layout_rows",
                "guitar_songs_layout_columns",
                "guitar_songs_layout_column_blocks"
              ]
            },
            "frontend": {
              "path": [
                "frontend/src/app/features/guitar/song/**"
              ]
            }
          }
        ]
      },
      {
        "package": "daily-journal",
        "description": {
          "en": [
            "- add a daily journal to any project, shared with all tribe members",
            "- write rich-text blocks for each day and label them to organize your notes; rename, recolour, reorder or delete labels from one panel",
            "- navigate between days with content, or browse all entries by label — blocks also appear in the global search",
            "- the planning dashboard shows journal activity and lets you jump straight to a day's journal"
          ],
          "fr": [
            "- ajoutez un journal quotidien à n'importe quel projet, partagé avec tous les membres de la tribu",
            "- rédigez des blocs de texte enrichi pour chaque jour et étiquetez-les pour organiser vos notes ; renommez, recolorez, réordonnez ou supprimez les étiquettes depuis un même panneau",
            "- naviguez entre les jours avec du contenu, ou parcourez toutes les entrées par étiquette — les blocs apparaissent aussi dans la recherche globale",
            "- le tableau de bord planning affiche l'activité du journal et permet d'accéder directement au journal d'un jour"
          ]
        },
        "backend": {
          "schema": [
            "journal_blocks"
          ],
          "path": [
            "backend/app/features/daily_journal/**"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/features/daily-journal/**"
          ]
        }
      },
      {
        "package": "recipes",
        "description": {
          "en": [
            "- keep a recipe book in a project tab, each recipe with a title, a base serving count, a difficulty level, a prep time and a total time, and a rich-text description",
            "- list ingredients from the shared groceries catalog with a quantity, searchable and grouped by food aisle, or add one-off ingredients that aren't in the catalog; mark an ingredient as an accompaniment (a suggested side, not part of the recipe itself) to keep it separate, and reorder ingredients to match how the recipe is written; ingredients are shown in two columns, with condiments and accompaniments each in their own section below the main ingredient list",
            "- compose a recipe out of another existing recipe (e.g. a Tart reusing a Pie Dough recipe), with how many times to use it; the reused recipe's ingredients and its own description appear in their own labeled section, both on screen and when printed",
            "- tag recipes with labels shared within the tab to organize and filter your recipe book; a configuration mode lets you rename, reorder or archive labels",
            "- opening a recipe for editing marks it as a draft (shown with a badge in the list) automatically, since editing means it's still being worked on; mark it as completed once it's ready, from the edit screen",
            "- search the recipe book by name or by ingredient, filter by a specific ingredient, filter by draft or completed, and filter by difficulty level",
            "- open a recipe in a clean read-only view, with a quick switch to an edit screen for the actual changes"
          ],
          "fr": [
            "- tenez un répertoire de recettes dans un onglet de projet, chaque recette avec un titre, un nombre de portions de base, un niveau de difficulté, un temps de préparation et un temps total, et une description enrichie",
            "- listez les ingrédients du catalogue de courses partagé avec une quantité, cherchables et regroupés par rayon alimentaire, ou ajoutez des ingrédients ponctuels absents du catalogue ; marquez un ingrédient comme accompagnement (une suggestion à part, qui ne fait pas partie de la recette elle-même) pour le distinguer, et réordonnez les ingrédients selon l'écriture de la recette ; les ingrédients s'affichent sur deux colonnes, condiments et accompagnements ayant chacun leur propre section sous la liste principale",
            "- composez une recette à partir d'une autre recette existante (ex. une Tarte réutilisant une recette de Pâte à Tarte), en précisant combien de fois l'utiliser ; les ingrédients de la recette réutilisée et sa propre description apparaissent dans leur propre section, à l'écran comme à l'impression",
            "- étiquetez les recettes avec des labels partagés dans l'onglet pour organiser et filtrer votre répertoire ; un mode configuration permet de renommer, réordonner ou archiver les labels",
            "- ouvrir une recette en édition la marque automatiquement comme brouillon (affiché avec un badge dans la liste), puisque l'éditer signifie qu'elle est encore en cours ; marquez-la comme terminée une fois prête, depuis l'écran d'édition",
            "- cherchez dans le répertoire par nom ou par ingrédient, filtrez par un ingrédient précis, filtrez par brouillon ou terminée, et filtrez par niveau de difficulté",
            "- ouvrez une recette dans une vue de lecture épurée, avec un accès rapide à un écran d'édition pour les modifications"
          ]
        },
        "backend": {
          "schema": [
            "recipes",
            "recipe_ingredients",
            "recipe_components"
          ],
          "path": [
            "backend/app/features/recipes/**"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/features/recipes/**"
          ]
        }
      },
      {
        "package": "meals",
        "description": {
          "en": [
            "- plan meals on a weekly view, one row per day with a morning/midday/evening slot to pick instead of exact times, an optional title, a description and how many people are eating (shown as a short tag, e.g. \"4p.\"); the number of days shown can be extended day by day before or after the week",
            "- name which team members are coming, independently of the headcount",
            "- link one or more recipes to a meal to know what's being cooked; recipe names on the weekly view open the recipe directly, and a linked recipe can also be opened in a read-only view from the meal",
            "- when a meal is planned after a scheduled shopping list, its recipes' ingredients appear as suggestions on that list — scaled to the meal's headcount — so nothing gets forgotten",
            "- add every ingredient of a suggested meal to the list in one click; the meal is remembered as added (with its headcount) and then shows greyed out among the suggestions — while shopping, see which meals this list already accounts for and for how many people",
            "- export the displayed meals plan as a PDF to print: a table of the plan on the first page (showing each meal's description when there is one), then one page per linked recipe with its ingredients and instructions"
          ],
          "fr": [
            "- planifiez des repas sur une vue hebdomadaire, une ligne par jour avec un créneau matin/midi/soir à choisir plutôt que des horaires précis, un titre optionnel, une description et le nombre de convives (affiché en abrégé, ex. « 4p. ») ; le nombre de jours affichés peut être étendu jour par jour avant ou après la semaine",
            "- indiquez quels membres de l'équipe sont présents, indépendamment du nombre de convives",
            "- reliez une ou plusieurs recettes à un repas pour savoir ce qui est cuisiné ; les noms des recettes sur la vue hebdomadaire ouvrent directement la recette, et une recette liée peut aussi s'ouvrir en lecture depuis le repas",
            "- quand un repas est planifié après une liste de courses programmée, les ingrédients de ses recettes apparaissent en suggestion sur cette liste — proportionnels au nombre de convives — pour ne rien oublier",
            "- ajoutez en un clic tous les ingrédients d'un repas suggéré à la liste de courses ; le repas est mémorisé comme ajouté (avec le nombre de convives) et apparaît alors grisé dans les suggestions — en mode course, consultez quels repas sont déjà pris en compte dans cette liste et pour combien de personnes",
            "- exportez le planning de repas affiché en PDF pour l'imprimer : un tableau du planning en première page (avec la description du repas quand il y en a une), puis une page par recette liée avec ses ingrédients et sa préparation"
          ]
        },
        "backend": {
          "schema": [
            "meals",
            "meal_participants",
            "meal_recipes",
            "groceries_list_meals"
          ],
          "path": [
            "backend/app/features/meals/**"
          ]
        },
        "frontend": {
          "path": [
            "frontend/src/app/features/meals/**"
          ]
        }
      },
      {
        "package": "language",
        "description": {
          "en": [
            "- Learn Language turns a project into a shared knowledge base to learn one language from another — French to English, for example",
            "- Together, a lesson's three tabs cover the whole learning loop: write the lesson, learn it, then test yourself on it — and they all draw from the exact same lessons and vocabulary, however many of each tab you add",
            "- an admin manages the list of languages available, and suggested translations while adding a word come from a dictionary built into the app, with nothing to connect or configure"
          ],
          "fr": [
            "- « Apprendre une langue » transforme un projet en base de connaissances partagée pour apprendre une langue à partir d'une autre — du français vers l'anglais, par exemple",
            "- les trois onglets couvrent ensemble tout le cycle d'apprentissage : rédiger la leçon, l'apprendre, puis se tester dessus — et ils puisent tous dans les mêmes leçons et le même vocabulaire, quel que soit le nombre d'onglets de chaque type",
            "- un administrateur gère la liste des langues disponibles, et les suggestions de traduction lors de l'ajout d'un mot proviennent d'un dictionnaire intégré à l'app, sans rien à connecter ni configurer"
          ]
        },
        "sub_packages": [
          {
            "package": "lesson",
            "description": {
              "en": [
                "- a lesson opens on two sub-tabs, \"The lesson\" for its title and write-up and \"The vocabulary\" for its word list as compact cards you can filter by word type — the sub-tab you were on is remembered",
                "- write a lesson's free-text content, exactly like any other rich-text document in the app",
                "- build its word list by picking a word already in the shared dictionary, or by creating a brand-new one with its translation, type, and — depending on the language — an article, a plural and a declension",
                "- a word you create becomes available to every project, so it's never retyped or retranslated twice",
                "- picking a word that already exists in another lesson brings its known translation along, so it is never retyped; the add-word page names the lesson it adds to",
                "- lessons are listed as compact cards, several per row, and the lesson you opened stays open when you switch between the lesson, learn and test tabs",
                "- remove a word from a lesson at any time — it only leaves that lesson and stays available everywhere else",
                "- every field where you type German (words, plurals, declension forms, test answers) has a ß button next to it to insert the character in one click",
                "- choose a color theme for each language: it colors every word of that language wherever it is shown or edited, in all projects",
                "- get a suggested translation from the app's built-in dictionary while typing a new word, with its own type and article confirmed as you type and, when known, the plural filled in (or type the plural in your own language and translate it with one click) — and when the dictionary has no match, you can choose to ask an online translation service (your typed word is only sent when you click) — adjust any of it freely before saving"
              ],
              "fr": [
                "- une leçon s'ouvre sur deux sous-onglets, « La leçon » pour son titre et son contenu et « Le vocabulaire » pour sa liste de mots en cartes compactes filtrables par type — le sous-onglet consulté est mémorisé",
                "- rédigez le contenu libre d'une leçon, comme n'importe quel autre document enrichi de l'app",
                "- construisez sa liste de mots en piochant un mot déjà présent dans le dictionnaire partagé, ou en créant un nouveau mot avec sa traduction, son type, et selon la langue, un article, un pluriel et une déclinaison",
                "- un mot que vous créez devient disponible pour tous les projets, il n'est jamais ressaisi ni retraduit deux fois",
                "- choisir un mot déjà présent dans une autre leçon reprend sa traduction connue, sans la ressaisir ; la page d'ajout d'un mot précise la leçon concernée",
                "- les leçons sont listées en cartes compactes, plusieurs par ligne, et la leçon ouverte reste ouverte quand vous passez d'un onglet leçon, apprendre ou test à un autre",
                "- retirez un mot d'une leçon à tout moment — il ne quitte que cette leçon et reste disponible partout ailleurs",
                "- chaque champ où vous saisissez de l'allemand (mots, pluriels, formes déclinées, réponses aux tests) a un bouton ß à côté pour insérer le caractère en un clic",
                "- choisissez un thème de couleur pour chaque langue : il colore chaque mot de cette langue partout où il est affiché ou édité, dans tous les projets",
                "- obtenez une suggestion de traduction du dictionnaire intégré à l'app pendant que vous tapez un nouveau mot, avec son type et son article confirmés au fil de la frappe et, quand il est connu, le pluriel pré-rempli (ou saisissez le pluriel dans votre langue et traduisez-le d'un clic) — et si le dictionnaire ne trouve rien, vous pouvez choisir d'interroger un service de traduction en ligne (le mot saisi n'est envoyé qu'au clic) — ajustez librement avant d'enregistrer"
              ]
            },
            "backend": {
              "schema": [
                "languages",
                "language_settings",
                "language_words",
                "language_word_declensions",
                "language_word_translations",
                "language_lessons",
                "language_lesson_words"
              ],
              "path": [
                "backend/app/features/language/lesson/**"
              ]
            },
            "frontend": {
              "path": [
                "frontend/src/app/features/language/lesson/**"
              ]
            }
          },
          {
            "package": "learn",
            "description": {
              "en": [
                "- read a lesson in a clean view made for learning, split into two sub-tabs like when writing it: the lesson itself, and its vocabulary as compact cards filterable by word type, each word shown with its translation",
                "- download the lesson as a printable PDF to study away from a screen, with the words grouped by type in the same compact cards, and the learned language, the date and page numbers in the footer"
              ],
              "fr": [
                "- lisez une leçon dans une vue épurée pensée pour l'apprentissage, découpée en deux sous-onglets comme à la rédaction : la leçon elle-même, et son vocabulaire en cartes compactes filtrables par type, chaque mot avec sa traduction",
                "- téléchargez la leçon en PDF imprimable pour l'étudier loin d'un écran, avec les mots regroupés par type dans les mêmes cartes compactes, et la langue apprise, la date et le numéro de page en bas de page"
              ]
            },
            "backend": {
              "path": [
                "backend/app/features/language/learn/**"
              ]
            },
            "frontend": {
              "path": [
                "frontend/src/app/features/language/learn/**"
              ]
            }
          },
          {
            "package": "test",
            "description": {
              "en": [
                "- a lesson's test page has six sub-tabs: two full tests, one per direction (e.g. French to German, then German to French), each going through every word once in random order with a word counter, a random test that comes back more often to the words you know least (without forgetting the well-known ones), an article test where you pick the article of a noun (e.g. der, die or das), a plural test where you type the plural of a noun, and your results — you answer either by typing the translation freely or by picking it among several words of the same type from the lesson",
                "- find out immediately whether your answer was right; when it wasn't, you get a varied, encouraging comment and the correct answer",
                "- each person's score for each word (including, for a noun with an article, knowing its article) goes from 0 to 1 as you keep answering it correctly in both directions over several different days — picking from choices helps much less than typing freely — and it slowly fades if you stop practicing a word",
                "- in the results, pick any member of the project (you by default) to see their big overall score, a chart of how it evolved over a duration you can reduce (week, month, 3 months), a collapsible explanation of how it is calculated and, for each word, its score and the history of their answers"
              ],
              "fr": [
                "- la page de test d'une leçon a six sous-onglets : deux tests complets, un par sens (par exemple français vers allemand, puis allemand vers français), qui passent chacun chaque mot une fois dans un ordre aléatoire avec un compteur de mots, un test aléatoire qui revient plus souvent sur les mots les moins bien sus (sans oublier les mieux connus), un test des articles où vous choisissez l'article d'un nom (par exemple der, die ou das), un test des pluriels où vous saisissez le pluriel d'un nom, et vos résultats — vous répondez en saisissant librement la traduction ou en la choisissant parmi plusieurs mots du même type de la leçon",
                "- découvrez immédiatement si votre réponse est juste ; si ce n'est pas le cas, un commentaire varié et encourageant s'affiche avec la bonne réponse",
                "- la note de chaque personne pour chaque mot (y compris, pour un nom avec article, la connaissance de son article) va de 0 à 1 à mesure que vous répondez juste dans les deux sens sur plusieurs jours différents — choisir parmi plusieurs options aide beaucoup moins que la saisie libre — et elle diminue doucement si vous arrêtez de pratiquer un mot",
                "- dans les résultats, choisissez n'importe quel membre du projet (vous par défaut) pour voir sa grosse note globale, un graphique de son évolution sur une durée réductible (semaine, mois, 3 mois), une explication repliable de son calcul et, pour chaque mot, sa note et l'historique de ses réponses"
              ]
            },
            "backend": {
              "schema": [
                "language_word_test_attempts"
              ],
              "path": [
                "backend/app/features/language/test/**"
              ]
            },
            "frontend": {
              "path": [
                "frontend/src/app/features/language/test/**"
              ]
            }
          }
        ]
      }
    ]
  },
  "dependencies": {
    "forbidden": "platform -> features"
  }
}
